import React, { useState, useEffect } from 'react';
import { activityAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { ProfilePhoto } from '../ProfilePhoto';
import { History, Coins, Zap, Trash2, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime, formatCoins } from '../../lib/utils';

export default function CompletionsHistoryTab({ classId }) {
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [completionToDelete, setCompletionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCompletions();
  }, [classId]);

  const loadCompletions = async () => {
    try {
      const response = await activityAPI.getCompletionsHistory(classId);
      setCompletions(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompletion = async () => {
    if (!completionToDelete) return;
    setDeleting(true);
    try {
      await activityAPI.deleteCompletion(classId, completionToDelete.id);
      toast.success('Registro eliminado y coins revertidos');
      setDeleteDialogOpen(false);
      setCompletionToDelete(null);
      loadCompletions();
    } catch (error) {
      toast.error('Error al eliminar registro');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (completion) => {
    setCompletionToDelete(completion);
    setDeleteDialogOpen(true);
  };

  const filteredCompletions = completions.filter(c => 
    c.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.activity_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-xl font-heading font-bold text-slate-900">Historial de Actividades Completadas</h3>
          <p className="text-slate-500">Revisa y gestiona las actividades otorgadas a estudiantes</p>
        </div>
        
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por estudiante o actividad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-completions"
          />
        </div>
      </div>

      {filteredCompletions.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <History className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">
              {searchTerm ? 'No se encontraron resultados' : 'No hay actividades completadas'}
            </p>
            <p className="text-sm text-slate-400">
              {searchTerm ? 'Intenta con otro término de búsqueda' : 'Las actividades otorgadas aparecerán aquí'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <History className="w-5 h-5" />
              {filteredCompletions.length} registros
            </CardTitle>
            <CardDescription>
              Puedes eliminar registros para revertir los coins otorgados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredCompletions.map((completion) => (
                <div 
                  key={completion.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid={`completion-${completion.id}`}
                >
                  <div className="flex items-center gap-4">
                    <ProfilePhoto 
                      photoUrl={completion.student_photo_url} 
                      name={completion.student_name} 
                      size={40} 
                    />
                    <div>
                      <p className="font-medium text-slate-900">{completion.student_name}</p>
                      <p className="text-sm text-slate-500">{completion.activity_title}</p>
                      <p className="text-xs text-slate-400">{formatDateTime(completion.created_at)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1 text-amber-600 font-bold">
                          <Coins className="w-4 h-4" />
                          +{formatCoins(completion.coins_awarded)}
                        </span>
                        {completion.activity_xp_value > 0 && (
                          <span className="flex items-center gap-1 text-purple-600 font-medium text-sm">
                            <Zap className="w-3 h-3" />
                            +{completion.activity_xp_value}XP
                          </span>
                        )}
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openDeleteDialog(completion)}
                      data-testid={`delete-completion-${completion.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Revertir Actividad Completada
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>¿Estás seguro de eliminar este registro?</p>
              {completionToDelete && (
                <div className="p-3 bg-slate-50 rounded-lg mt-2">
                  <p className="font-medium">{completionToDelete.student_name}</p>
                  <p className="text-sm text-slate-500">{completionToDelete.activity_title}</p>
                  <p className="text-sm text-red-600 mt-1">
                    Se revertirán {formatCoins(completionToDelete.coins_awarded)} coins
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteCompletion} 
              disabled={deleting}
              data-testid="confirm-delete-completion"
            >
              {deleting ? 'Eliminando...' : 'Eliminar y Revertir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
